/**
 * AcceptInvite - Public page for accepting user invitations
 * PROJ-032: User Invitation System
 *
 * This page is accessed via the invitation URL sent by email.
 * It allows the user to:
 * - Set a local password, OR
 * - Authenticate via Google/Microsoft CIAM (OAuth popup)
 */

import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Eye,
  EyeOff,
  Lock,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { GoogleButton } from '@/components/auth/GoogleButton';
import { MicrosoftButton } from '@/components/auth/MicrosoftButton';
import { openOAuthPopup } from '@/hooks/useOAuthPopup';
import {
  validateInvitation,
  completeInvitation,
  resendInvitationPublic,
  type ValidateInvitationResponse,
} from '@/lib/invitationApi';
import { Mail, RefreshCw } from 'lucide-react';

type PageState = 'loading' | 'valid' | 'expired' | 'used' | 'invalid' | 'success' | 'error';

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [validationData, setValidationData] = useState<ValidateInvitationResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Password form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string>('');

  // OAuth state
  const [oauthLoading, setOauthLoading] = useState<'google' | 'microsoft' | null>(null);

  // Resend invitation state
  const [expiredEmail, setExpiredEmail] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      setErrorMessage('No se proporcionó un token de invitación válido.');
      return;
    }

    async function validate() {
      try {
        const data = await validateInvitation(token!);
        setValidationData(data);
        setPageState('valid');
      } catch (error: any) {
        console.error('[AcceptInvite] Validation error:', error);
        const code = error.code || 'UNKNOWN_ERROR';
        const details = error.details || {};
        if (code === 'INVITATION_EXPIRED') {
          setPageState('expired');
          setErrorMessage('Esta invitación ha expirado.');
          if (details.userEmail) setExpiredEmail(details.userEmail);
        } else if (code === 'INVITATION_ALREADY_USED') {
          setPageState('used');
          setErrorMessage('Esta invitación ya fue utilizada. Si ya tienes una cuenta, intenta iniciar sesión.');
          if (details.userEmail) setExpiredEmail(details.userEmail);
        } else {
          setPageState('invalid');
          setErrorMessage(error.message || 'Token de invitación inválido o no encontrado.');
        }
      }
    }

    validate();
  }, [token]);

  // Handle OAuth popup message (success or error)
  const handleOAuthMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      const { type, error: oauthError } = event.data || {};

      // Handle OAuth error from popup (e.g. USER_NOT_REGISTERED)
      if (type === 'oauth_error') {
        setFormError(oauthError || 'Error de autenticación. Por favor intenta nuevamente.');
        setOauthLoading(null);
        return;
      }

      if (type !== 'oauth_success') return;
      if (!token || !oauthLoading) return;

      const authMethod = oauthLoading;
      try {
        await completeInvitation({
          token,
          authMethod,
        });
        // User is already logged in from OAuth - go straight to dashboard
        navigate('/dashboard', { replace: true });
      } catch (error: any) {
        console.error('[AcceptInvite] OAuth completion error:', error);
        setFormError(error.message || 'Error al completar el registro con CIAM');
        setOauthLoading(null);
      }
    },
    [token, oauthLoading, navigate]
  );

  useEffect(() => {
    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [handleOAuthMessage]);

  const handleOAuthClick = (provider: 'google' | 'microsoft') => {
    setFormError('');
    setOauthLoading(provider);

    const url = provider === 'google' ? '/api/v1/auth/google/login' : '/api/v1/auth/azure/login';

    // Store popup flag so AuthCallback knows to postMessage
    sessionStorage.setItem('oauth_provider', provider === 'microsoft' ? 'azure' : provider);
    sessionStorage.setItem('oauth_popup', 'true');

    const popup = openOAuthPopup({
      url,
      name: `${provider}-oauth`,
    });

    if (!popup) {
      // Popup blocked - can't use redirect for invite flow, show error
      setFormError('El navegador bloqueó la ventana emergente. Permite ventanas emergentes e intenta de nuevo.');
      setOauthLoading(null);
      sessionStorage.removeItem('oauth_popup');
    }
  };

  const validatePassword = (): string | null => {
    if (!password) {
      return 'La contraseña es requerida';
    }
    if (password.length < 8) {
      return 'La contraseña debe tener al menos 8 caracteres';
    }
    if (password !== confirmPassword) {
      return 'Las contraseñas no coinciden';
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');

    const validationError = validatePassword();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      await completeInvitation({
        token: token!,
        authMethod: 'local',
        password,
      });
      setPageState('success');
    } catch (error: any) {
      console.error('[AcceptInvite] Completion error:', error);
      setFormError(error.message || 'Error al completar el registro');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const isBusy = isSubmitting || oauthLoading !== null;

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Validando invitación...</p>
        </div>
      </div>
    );
  }

  // Handle resend invitation
  const handleResendInvitation = async () => {
    if (!expiredEmail || resendLoading) return;
    setResendLoading(true);
    try {
      await resendInvitationPublic(expiredEmail);
      setResendSent(true);
    } catch {
      // Even on error, show success (anti-enumeration)
      setResendSent(true);
    } finally {
      setResendLoading(false);
    }
  };

  // Error states (expired, used, invalid)
  if (pageState === 'expired' || pageState === 'used' || pageState === 'invalid') {
    // Resend success state
    if (resendSent) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                <Mail className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle>Revisa tu correo</CardTitle>
              <CardDescription>
                Si <span className="font-medium text-foreground">{expiredEmail}</span> está
                registrado en la plataforma, recibirás un nuevo enlace de invitación.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-center">
              <p className="text-sm text-muted-foreground">
                El nuevo enlace expirará en 72 horas. Revisa también tu carpeta de spam.
              </p>
              <Button variant="outline" onClick={() => navigate('/')}>
                Ir al Inicio
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle>
              {pageState === 'expired' && 'Invitación Expirada'}
              {pageState === 'used' && 'Invitación Ya Utilizada'}
              {pageState === 'invalid' && 'Invitación Inválida'}
            </CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-3">
            {/* Show resend button for expired invitations when we have the email */}
            {pageState === 'expired' && expiredEmail && (
              <Button onClick={handleResendInvitation} disabled={resendLoading}>
                {resendLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Solicitar Nueva Invitación
                  </>
                )}
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/')}>
              Ir al Inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state (only for local password flow)
  if (pageState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
              <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle>Cuenta Activada</CardTitle>
            <CardDescription>
              Tu cuenta ha sido activada exitosamente. Ya puedes iniciar sesión con tu email y contraseña.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/')}>
              Iniciar Sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid - show registration form with CIAM + password options
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Completa tu Registro</CardTitle>
          <CardDescription>
            Elige cómo deseas acceder a tu cuenta
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* User info */}
          {validationData && (
            <div className="mb-6 rounded-lg border bg-muted/50 p-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-12 w-12">
                  <AvatarFallback>{getInitials(validationData.fullName)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{validationData.fullName}</p>
                  <p className="text-sm text-muted-foreground truncate">{validationData.userEmail}</p>
                </div>
              </div>
              {validationData.hoursRemaining > 0 && (
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span>Esta invitación expira en {validationData.hoursRemaining} horas</span>
                </div>
              )}
            </div>
          )}

          {formError && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          {/* Social login buttons */}
          <div className="space-y-3">
            <GoogleButton
              onClick={() => handleOAuthClick('google')}
              disabled={isBusy}
            />
            <MicrosoftButton
              onClick={() => handleOAuthClick('microsoft')}
              disabled={isBusy}
            />
          </div>

          {oauthLoading && (
            <div className="mt-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Esperando autenticación...</span>
            </div>
          )}

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">
                o crea una contraseña
              </span>
            </div>
          </div>

          {/* Password form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  className="pl-9 pr-10"
                  disabled={isBusy}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  className="pl-9"
                  disabled={isBusy}
                />
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={isBusy}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Activando cuenta...
                </>
              ) : (
                'Activar con Contraseña'
              )}
            </Button>
          </form>

          <p className="mt-4 text-center text-xs text-muted-foreground">
            Al activar tu cuenta, aceptas nuestros términos de servicio y política de privacidad.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
